import type { ReactNode } from 'react'
import Container from './Container'
import Reveal from './Reveal'

interface PageHeaderProps {
  /** ReactNode, not string: a label may carry its own line breaks. */
  tag?: ReactNode
  title: ReactNode
  description?: ReactNode
  className?: string
  /**
   * Extra content under the description, inside the container.
   *
   * Used by the ValuesIncubator+ page for the Priority Values colour-code
   * stripe. Kept optional so every other page's header is unchanged.
   */
  children?: ReactNode
}

/**
 * PageHeader — consistent header for non-Home pages
 * Mirrors Chioary page-header style
 *
 * The visible "首頁 / 當前頁面" trail was removed on 2026-11-03 (it was
 * rendered here for every public inner page). The `tag` / `title` lines stay
 * as they are. The SEO `BreadcrumbList` JSON-LD is emitted separately by
 * `buildBreadcrumbList()` and is intentionally left in place.
 *
 * 2026-11-04: the `hideTitle` prop is gone. It was added on 2026-11-03 to
 * stop painting the `<h1>` on /products and /lectures, and both pages now
 * paint their title again. The `<h1>` is always visible: a page needs
 * exactly one, and a reader needs to see it.
 */
export default function PageHeader({ tag, title, description, children, className }: PageHeaderProps) {
  return (
    <section
      className={`relative bg-gradient-to-br from-surface via-surface-2 to-surface py-section-y px-section-x overflow-hidden ${className ?? ''}`}
      aria-labelledby="page-title"
    >
      {/* Decorative sparkles */}
      <div
        aria-hidden="true"
        className="absolute top-12 right-12 w-20 h-20 text-accent opacity-50"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0L14 10L24 12L14 14L12 24L10 14L0 12L10 10Z" />
        </svg>
      </div>
      <div
        aria-hidden="true"
        className="absolute bottom-16 left-16 w-14 h-14 text-ink-soft opacity-30"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0L14 10L24 12L14 14L12 24L10 14L0 12L10 10Z" />
        </svg>
      </div>

      <Container>
        <Reveal>
          {tag && (
            <span className="section-title__tagline inline-block mb-4">{tag}</span>
          )}
          <h1
            id="page-title"
            className="hero-title text-3xl md:text-4xl lg:text-5xl text-ink max-w-4xl"
          >
            {title}
          </h1>
          {description && (
            <p className="mt-4 text-lg md:text-xl text-ink-soft max-w-3xl">{description}</p>
          )}
          {children}
        </Reveal>
      </Container>
    </section>
  )
}
